import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import OtherCostFormDialog from '../detailDialogForms/OtherCostFormDialog';
import {
    useAddWarrantyClaimsOtherCostMutation,
    useGetWarrantyClaimsByIdQuery,
    useGetWarrantyClaimsOtherCostListQuery,
    useUpdateWarrantyClaimsOtherCostMutation,
    useDeleteWarrantyClaimsOtherCostMutation,
    useCalculateWarrantyClaimTotalsQuery
} from '../../../../../../../api';
import { useLazyGetWarrantyClaimsOtherCostItemPdfQuery } from '../../../../../../../api/endpoints/warrantyClaims';
import { useLanguage } from '../../../../../../../contexts/LanguageContext';
import { usePermissions } from '../../../../../../../hooks/usePermissions';
import { WarrantyClaimStatus } from '../../../../../../../utils/warrantyClaimsEnums';
import PdfGenerationService from '../../../../../../../utils/pdfGenerationService';

const DISABLED_STATUSES = [WarrantyClaimStatus.RETURNED_TO_KARSAN, WarrantyClaimStatus.ACCEPTED, WarrantyClaimStatus.REJECTED];

const OtherCostTable = ({ claimId, authorizationCheck = false }) => {
    const { th, getCurrentLanguageId } = useLanguage();
    const { hasDealerRole, hasServiceDealerRole, hasSalesDealerRole } = usePermissions();
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editingOtherCost, setEditingOtherCost] = useState(null);
    const toastRef = useRef(null);
    const [triggerGetOtherCostItemPdf, { isLoading: isLoadingPdf }] = useLazyGetWarrantyClaimsOtherCostItemPdfQuery();
    const pdfService = useMemo(() => new PdfGenerationService(), []);

    const [lazyParams, setLazyParams] = useState({
        rows: 10,
        sortField: null,
        sortOrder: null
    });

    const {
        data: apiWarrantyClaimsOtherCostList,
        isLoading,
        refetch
    } = useGetWarrantyClaimsOtherCostListQuery({
        WarrantyClaimsId: claimId,
        PageSize: 1000,
        PageNumber: 1,
        LanguageId: getCurrentLanguageId()
    });

    const { data: warrantyClaimGetById } = useGetWarrantyClaimsByIdQuery({ id: claimId, languageId: getCurrentLanguageId() });
    const { refetch: refetchTotals } = useCalculateWarrantyClaimTotalsQuery({ warrantyClaimId: claimId }, { skip: !claimId });
    const [addWarrantyClaimsOtherCost] = useAddWarrantyClaimsOtherCostMutation();
    const [updateWarrantyClaimsOtherCost] = useUpdateWarrantyClaimsOtherCostMutation();
    const [deleteWarrantyClaimsOtherCost] = useDeleteWarrantyClaimsOtherCostMutation();

    const disabledStatuses = DISABLED_STATUSES.includes(warrantyClaimGetById?.status);
    const disabledForRole = (hasDealerRole() || hasServiceDealerRole() || hasSalesDealerRole()) && warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING;
    useEffect(() => {
        if (claimId) {
            refetch();
        }
    }, [claimId, refetch]);
    const sortedAndPagedData = useMemo(() => {
        const allItems = apiWarrantyClaimsOtherCostList?.data?.items || [];

        let sortedData = [...allItems];
        if (lazyParams.sortField && lazyParams.sortOrder !== 0) {
            sortedData.sort((a, b) => {
                let aValue = a[lazyParams.sortField];
                let bValue = b[lazyParams.sortField];

                if (aValue == null && bValue == null) return 0;
                if (aValue == null) return lazyParams.sortOrder;
                if (bValue == null) return -lazyParams.sortOrder;

                if (typeof aValue === 'string' && typeof bValue === 'string') {
                    aValue = aValue.toLowerCase();
                    bValue = bValue.toLowerCase();
                }

                if (typeof aValue === 'number' && typeof bValue === 'number') {
                    return lazyParams.sortOrder * (aValue - bValue);
                }

                if (aValue < bValue) return -lazyParams.sortOrder;
                if (aValue > bValue) return lazyParams.sortOrder;
                return 0;
            });
        }

        return sortedData;
    }, [apiWarrantyClaimsOtherCostList?.data?.items, lazyParams.sortField, lazyParams.sortOrder]);

    const handleNewOtherCost = () => {
        setEditingOtherCost(null);
        setDialogVisible(true);
    };

    const handleSave = async (data) => {
        try {
            let result;
            if (data?.id) {
                result = await updateWarrantyClaimsOtherCost(data).unwrap();
            } else {
                const { id, ...otherCostData } = data;
                result = await addWarrantyClaimsOtherCost(otherCostData).unwrap();
            }

            if (result) {
                refetch();
                refetchTotals();
                setDialogVisible(false);
                return true;
            }

            return false;
        } catch (error) {
            console.error('Diğer maliyet kaydedilirken hata:', error);
        }
    };

    const handleDelete = async (id) => {
        try {
            const result = await deleteWarrantyClaimsOtherCost(id).unwrap();
            if (result) {
                refetch();
                refetchTotals();
                setDialogVisible(false);
                return true;
            }
            return false;
        } catch (error) {
            console.error('Diğer maliyet silinirken hata:', error);
        }
    };

    const handleEdit = (rowData) => {
        setEditingOtherCost(rowData);
        setTimeout(() => {
            setDialogVisible(true);
        }, 0);
    };

    const onSort = (event) => {
        setLazyParams((prev) => ({
            ...prev,
            sortField: event.sortField,
            sortOrder: event.sortOrder
        }));
    };

    const handleViewPdf = useCallback(
        async () => {
            const claimId = warrantyClaimGetById?.id;

            if (!claimId) {
                toastRef.current?.show({
                    severity: 'error',
                    summary: th('common.error'),
                    detail: th('warrantyClaims.pdf.missingData'),
                    life: 3000
                });
                return;
            }

            try {
                const result = await triggerGetOtherCostItemPdf({ claimId }).unwrap();

                if (result?.success && result?.data) {
                    const translations = {
                        title: th('warrantyClaims.pdf.title'),
                        service: th('warrantyClaims.pdf.service'),
                        claimNo: th('warrantyClaims.pdf.claimNo'),
                        faultKm: th('warrantyClaims.pdf.faultKm'),
                        repairKm: th('warrantyClaims.pdf.repairKm'),
                        chassisNo: th('warrantyClaims.pdf.chassisNo'),
                        warrantyStart: th('warrantyClaims.pdf.warrantyStart'),
                        plateNo: th('warrantyClaims.pdf.plateNo'),
                        repairNoDate: th('warrantyClaims.pdf.repairNoDate'),
                        faultDate: th('warrantyClaims.pdf.faultDate'),
                        repairDate: th('warrantyClaims.pdf.repairDate'),
                        causeOfFailure: th('warrantyClaims.pdf.causeOfFailure'),
                        customerStatement: th('warrantyClaims.pdf.customerStatement'),
                        itemDescription: th('warrantyClaims.detail.otherCosts.description'),
                        authorizedService: th('warrantyClaims.pdf.authorizedService'),
                        stampSignature: th('warrantyClaims.pdf.stampSignature'),
                        date: th('warrantyClaims.pdf.date')
                    };

                    const pdfDoc = pdfService.generateOtherCostClaimCardPdf(result.data, translations);
                    pdfService.viewPdf(pdfDoc);
                } else {
                    toastRef.current?.show({
                        severity: 'error',
                        summary: th('common.error'),
                        detail: th('warrantyClaims.pdf.pdfError'),
                        life: 3000
                    });
                }
            } catch (error) {
                console.error('PDF oluşturma hatası:', error);
                toastRef.current?.show({
                    severity: 'error',
                    summary: th('common.error'),
                    detail: th('warrantyClaims.pdf.pdfError'),
                    life: 3000
                });
            }
        },
        [triggerGetOtherCostItemPdf, pdfService, th, warrantyClaimGetById?.id]
    );


    const actionBodyTemplate = (rowData) => {
        const showPdfButton = rowData.selectType === 1;

        return (
            <div className="flex gap-2 justify-content-center">
                <i
                    disabled={DISABLED_STATUSES.includes(warrantyClaimGetById?.status)}
                    onClick={() => handleEdit(rowData)}
                    className="pi pi-pencil cursor-pointer text-blue-500 hover:text-blue-700"
                    style={{ fontSize: '1.1rem' }}
                    title={th('warrantyClaims.detail.otherCosts.edit')}
                ></i>
                {showPdfButton && (
                    <i
                        onClick={() => !isLoadingPdf && handleViewPdf()}
                        className={`pi pi-file-pdf ${isLoadingPdf ? 'cursor-wait text-gray-400' : 'cursor-pointer text-red-500 hover:text-red-700'}`}
                        style={{ fontSize: '1.1rem' }}
                        title={th('warrantyClaims.pdf.viewPdf')}
                    ></i>
                )}
            </div>
        );
    };
    const formatCurrency = (value) => {
    if (value == null) return '-';
    return new Intl.NumberFormat('tr-TR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(value);
};

    return (
        <div>
            <Toast ref={toastRef} />
            <div className="flex justify-content-start align-items-center mb-3 mt-3">
                <Button label={th('warrantyClaims.detail.otherCosts.addNew')} icon="pi pi-plus" onClick={handleNewOtherCost} disabled={DISABLED_STATUSES.includes(warrantyClaimGetById?.status) || warrantyClaimGetById?.status === WarrantyClaimStatus.WAITING} />
            </div>
            <DataTable
                value={sortedAndPagedData}
                className="p-datatable-gridlines"
                showGridlines
                paginator
                rows={lazyParams.rows}
                onSort={onSort}
                sortField={lazyParams.sortField}
                sortOrder={lazyParams.sortOrder}
                dataKey="id"
                loading={isLoading}
                emptyMessage={th('warrantyClaims.detail.otherCosts.emptyMessage')}
                rowsPerPageOptions={[5, 10, 25, 50]}
                tableStyle={{ minWidth: '60rem' }}
            >
                <Column body={actionBodyTemplate} style={{ width: '5%', minWidth: '33px' }} bodyClassName="text-center" headerClassName="text-center" exportable={false} />
                <Column field="description" header={th('warrantyClaims.detail.otherCosts.description')} sortable />
                <Column field="selectTypeName" header={th('warrantyClaims.detail.otherCosts.selectType')} sortable />
                <Column
                    field="partQuantity"
                    header={th('warrantyClaims.detail.otherCosts.partQuantity')}
                    sortable
                    body={(rowData) => ((rowData.selectType === 1 || rowData.selectType === 2) ? (rowData.partQuantity ?? '-') : '-')}
                />
                <Column
                    field="hour"
                    header={th('warrantyClaims.detail.otherCosts.hour')}
                    sortable
                    body={(rowData) => (rowData.selectType === 2 ? (rowData.hour ?? '-') : '-')}
                />
                <Column field="warrantyTypeName" header={th('warrantyClaims.detail.partDialog.warrantyType')} sortable />
                <Column field="price" header={th('warrantyClaims.detail.otherCosts.price')} sortable  body={(rowData) => formatCurrency(rowData.price)} />
                <Column sortable field="totalAmount" header={th('warrantyClaims.detail.partTable.totalAmount')} body={(rowData) => formatCurrency(rowData.totalAmount)} />
                <Column sortable field="acceptedAmount" header={th('warrantyClaims.detail.partTable.acceptedAmount')} body={(rowData) => formatCurrency(rowData.acceptedAmount)} />
                <Column field="currency" header={th('warrantyClaims.detail.otherCosts.currency')} sortable />
            </DataTable>

            <OtherCostFormDialog
                disabledStatuses={disabledStatuses || disabledForRole}
                claimStatus={warrantyClaimGetById?.status}
                authorizationCheck={authorizationCheck}
                claimId={claimId}
                warrType={warrantyClaimGetById?.warrType}
                isDomestic={warrantyClaimGetById?.isDomestic}
                dialogVisible={dialogVisible}
                hideDialog={() => setDialogVisible(false)}
                onSave={handleSave}
                onDelete={handleDelete}
                editingOtherCost={editingOtherCost}
                onUpdateSuccess={() => {
                    // Update işleminden sonra liste verilerini yenile
                    refetch();
                }}
            />
        </div>
    );
};

export default OtherCostTable;
